import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuthStore from '../../../stores/authStore';
import { PROJECTEN_LINK } from './navigationConfig';

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="4" fill="currentColor" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="currentColor" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      className="c-nav-user__chevron"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatDisplayName(username) {
  if (!username) return '';
  if (username.includes('@')) {
    return username.split('@')[0].replace(/[._-]/g, ' ');
  }
  return username.replace(/[._-]/g, ' ');
}

function NavUserMenu({ onNavigate }) {
  const menuId = useId();
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        close();
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const handleNavigate = () => {
    close();
    onNavigate?.();
  };

  const handleLogout = () => {
    logout();
    handleNavigate();
    navigate('/login');
  };

  const displayName = formatDisplayName(user?.username);

  return (
    <div ref={rootRef} className={`c-nav-user${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="c-nav-user__toggle"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="menu"
        aria-label={displayName ? `Accountmenu voor ${displayName}` : 'Accountmenu'}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="c-nav-user__avatar">
          <UserIcon />
        </span>
      </button>

      <div id={menuId} className="c-nav-user__menu" role="menu" hidden={!open}>
        {displayName && (
          <p className="c-nav-user__name" role="presentation">
            {displayName}
          </p>
        )}
        <button type="button" className="c-nav-user__item" role="menuitem" onClick={handleLogout}>
          Uitloggen
        </button>
        <Link
          to={PROJECTEN_LINK.path}
          className="c-nav-user__item c-nav-user__item--link"
          role="menuitem"
          onClick={handleNavigate}
        >
          <span>Mijn projecten</span>
          <ChevronRightIcon />
        </Link>
      </div>
    </div>
  );
}

export default NavUserMenu;
